import type { FlipPad, Hazard } from '@shared/chart';
import { hexToRgba } from './pulseRenderColour';

/**
 * Stateless shape drawers for hazards + pads. Extracted as functions
 * (not class methods) because they don't need any renderer state
 * beyond the canvas context — the caller threads through positions,
 * sizes, and colours.
 */

export function drawHazardShape(
  ctx: CanvasRenderingContext2D,
  h: Hazard,
  startX: number,
  endX: number,
  baseY: number,
  scaleY: number,
  colour: string,
): void {
  ctx.save();
  ctx.shadowColor = colour;
  ctx.shadowBlur = 12;
  ctx.fillStyle = colour;
  if (h.kind === 'spike') {
    const spikeH = 18 * scaleY;
    const dir = h.surface === 'floor' ? -1 : 1;
    const tipY = baseY + dir * spikeH;
    const span = endX - startX;
    const spikeW = 14;
    const count = Math.max(1, Math.floor(span / spikeW));
    for (let i = 0; i < count; i++) {
      const sx = startX + i * spikeW;
      ctx.beginPath();
      ctx.moveTo(sx, baseY);
      ctx.lineTo(sx + spikeW / 2, tipY);
      ctx.lineTo(sx + spikeW, baseY);
      ctx.closePath();
      ctx.fill();
    }
  } else if (h.kind === 'laser') {
    ctx.fillRect(startX, baseY - 2, endX - startX, 4);
  } else {
    ctx.fillRect(startX, baseY - 4, endX - startX, 8);
  }
  ctx.restore();
}

/**
 * Pad shape with optional good-window halo. Layout:
 *
 *   • Good-window halo (skipped on hit / optional pads) — a flat
 *     low-alpha gradient running the full good-window width centred
 *     on the pad's beat. Shows the player how much slop they have
 *     either side of the perfect core.
 *
 *   • Pad core — a coloured rectangle anchored at the pad's surface
 *     (floor / ceiling), sized to the smaller of the chart-tuned
 *     ideal width and the actual good-window. Floored at `size` so
 *     the pad stays visible against the player block in extreme
 *     edge cases (narrow viewport + slow chart).
 *
 *   • Approach ring (skipped on hit) — a square outline at the pad
 *     position; thicker on imminent pads via shadow blur. The
 *     fixed-pixel +24 inset gives the ring noticeable breathing room
 *     around the core.
 *
 *   • Optional-pad "+" — small mid-ring glyph telling the player
 *     "you don't have to hit me but you'll be rewarded if you do".
 */
export function drawPadShape(
  ctx: CanvasRenderingContext2D,
  pad: FlipPad,
  x: number,
  y: number,
  size: number,
  hit: boolean,
  palette: { pad: string; padPerfect: string; ui: string },
  beat: number,
  goodHalfPx: number,
): void {
  const distance = pad.beat - beat;
  const isImminent = distance < 0.15 && distance > -0.15;
  const colour = pad.optional ? palette.padPerfect : palette.pad;
  ctx.save();
  if (hit) {
    ctx.globalAlpha = 0.2;
  }
  const pulse = Math.max(0.5, 1 - Math.abs(distance) * 0.6);
  const preGlow = !hit && distance > 0 && distance < 0.5 ? 1 - distance / 0.5 : 0;

  const padHeight = pad.optional ? 6 : 9;
  // Cap the pad core at the good-window width. The +16 padding is a
  // fixed pixel constant tuned for desktop, but xPerBeat (and therefore
  // goodHalfPx) scales linearly with canvas width. On narrow viewports
  // (portrait mobile) and slow charts the unscaled +16 makes the
  // visible box wider than the actual ±GOOD timing zone, so a tap
  // while the pad overlaps the player can already be past the good
  // window and register as a miss. Floored at `size` so the pad stays
  // visible against the player block in extreme edge cases.
  const idealPadWidth = size + 16;
  const padWidth = Math.min(idealPadWidth, Math.max(goodHalfPx * 2, size));
  const yOffset = pad.surface === 'floor' ? -padHeight : 0;

  // Good-window halo — a flatter, low-alpha extension that runs the
  // full good-window width centred on the pad's beat. Tells the
  // player exactly how much slop they have either side of the perfect
  // core. Skipped on already-hit pads (they fade out via globalAlpha)
  // and on optional pads (lighter visual weight to keep them
  // distinguishable).
  if (!hit && !pad.optional && goodHalfPx > padWidth / 2) {
    const haloW = goodHalfPx * 2;
    const haloH = padHeight + 4;
    const haloY = pad.surface === 'floor' ? -haloH : 0;
    const grad = ctx.createLinearGradient(x - haloW / 2, 0, x + haloW / 2, 0);
    grad.addColorStop(0, hexToRgba(colour, 0.0));
    grad.addColorStop(0.2, hexToRgba(colour, 0.18));
    grad.addColorStop(0.5, hexToRgba(colour, 0.32));
    grad.addColorStop(0.8, hexToRgba(colour, 0.18));
    grad.addColorStop(1, hexToRgba(colour, 0.0));
    ctx.fillStyle = grad;
    ctx.shadowBlur = 0;
    ctx.fillRect(x - haloW / 2, y + haloY, haloW, haloH);
  }

  ctx.shadowColor = colour;
  ctx.shadowBlur = isImminent ? 28 : 14 + preGlow * 18;
  ctx.fillStyle = colour;
  ctx.fillRect(x - padWidth / 2, y + yOffset, padWidth, padHeight);

  if (!hit) {
    ctx.globalAlpha = pulse * 0.7;
    const ringSize = size + 24;
    const ringY = pad.surface === 'floor' ? y - ringSize - 2 : y + padHeight + 2;
    ctx.strokeStyle = colour;
    ctx.lineWidth = 2;
    ctx.strokeRect(x - ringSize / 2, ringY, ringSize, ringSize);

    if (pad.optional) {
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = palette.ui;
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('+', x, ringY + ringSize / 2 + 3);
    }
  }
  ctx.restore();
}
